import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Image from "components/atoms/Image";
import Button from "components/atoms/Button";
import { DrugProductLabel } from "common/models/types";
import {
  MODULE_NAME_MAP,
  SUBMODULE_NAME_MAP,
  SUBMODULE_ORDER,
  baseCode,
  sectionDomId,
  subSectionDomId,
  buildSectionToc,
} from "common/constants/drugLabelModules";
import parse from "html-react-parser";
import { log, LogLevel } from "utils/LogUtil";
import { getLabelImageUrl, LABEL_IMAGE_PLACEHOLDER_PATTERN } from "utils/drugImageUtils";

interface DrugLabelInfoProps {
  labelInfo: DrugProductLabel[];
  showMetadata?: boolean; // admin 모드에서만 메타데이터 태그 표시
  itemCode?: string;
  standardCode?: string;
  mainCode?: string;
  productName?: string; // 제품명 추가
  moduleNameMap?: Record<string, string>; // 건기식 등 맞춤 모듈명 매핑
  /** 코드 단위 필터(Phase 3). 실제 코드 배열. 미전달(undefined)=전체 표시(하위호환).
   *  비-NB 항목은 module ∈ selectedCodes, NB 항목은 submodule ∈ selectedCodes 일 때만 표시. */
  selectedCodes?: string[];
  /** 목차 점프 콜백 — 전달 시 부모가 보고서 ref와 동일하게 스크롤+하이라이트 처리(권장).
   *  미전달 시 자체 scrollIntoView만(다른 사용처). */
  onTocJump?: (targetId: string) => void;
  onHeaderMount?: (itemCode: string, element: HTMLElement) => void; // 헤더 마운트 시 콜백
  onHeaderVisible?: (itemCode: string, isVisible: boolean, ratio: number) => void; // 헤더 가시성 변경 시 콜백
}

const Container = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  background: #fff;

  table {
    border-collapse: collapse;
    border-spacing: 0;
  }

  /* caption을 테이블 전체 너비로 표시 */
  caption,
  .enhanced-caption {
    display: table-caption;
    caption-side: top;
    text-align: left;
    padding: 16px 0 8px 0;
    background-color: transparent;
    border: none;
    font-weight: 400;
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
    font-size: 13px;
    line-height: 1.5;
    width: 100%;
    box-sizing: border-box;

    /* caption 내부의 모든 내용을 텍스트로만 표시 */
    * {
      display: inline;
      font-size: inherit;
      font-weight: inherit;
      color: inherit;
    }

    /* 숨겨진 span의 텍스트는 표시 */
    .hidden {
      display: inline;
    }

    /* p 태그 스타일 제거 */
    p {
      margin: 0;
      display: inline;
    }
  }

  .enhanced-tr-header {
    background: ${COLOR_SYSTEM.get("Gray")?.[10]} !important;
  }

  td,
  th {
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]} !important;
    text-align: center;
    vertical-align: middle;
  }
`;

const ProductHeader = styled.div`
  padding: 5px;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  word-break: break-word;
  overflow-wrap: break-word;

  .header-title-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
  }

  h2 {
    margin: 0;
    font-weight: 600;
    color: ${COLOR_SYSTEM.get("Blue")?.[500]};
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.3;
  }

  .NedrugUrlJump {
    font-size: 12px;
    display: flex;
    align-items: center;
    min-height: 20px;

    a {
      color: ${COLOR_SYSTEM.get("Gray")?.[600]};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
    span {
      margin-right: 16px;
    }
  }
`;

const ProductSection = styled.div`
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 6px;
`;

const Section = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionHeader = styled.div`
  margin-bottom: 12px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding-bottom: 8px;
`;

// 하위 섹션 스타일
const SubSection = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const SubSectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  padding-left: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  border-left: 3px solid ${COLOR_SYSTEM.get("Blue")?.[400]};
  /* 단, 목차/ref 하이라이트 중에는 회색 배경을 비워 파란 하이라이트가 소제목까지 비치게 */
  .reference-highlight & {
    background-color: transparent;
  }
`;

// 약 내부 목차(TOC) — sticky 제품명 헤더 우측. 그 약의 항목으로 점프.
const TocContainer = styled.div`
  position: relative;
  display: inline-flex;
  margin-left: auto;
  flex-shrink: 0;
`;

const TocMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 20;
  min-width: 180px;
  max-width: 280px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
`;

const TocMenuItem = styled.button<{ $depth: number }>`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ $depth }) => ($depth === 0 ? 13 : 12)}px;
  font-weight: ${({ $depth }) => ($depth === 0 ? 600 : 400)};
  color: ${({ $depth }) =>
    $depth === 0 ? COLOR_SYSTEM.get("Gray")?.[800] : COLOR_SYSTEM.get("Gray")?.[600]};
  padding: 6px 12px;
  padding-left: ${({ $depth }) => 12 + $depth * 16}px;
  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const Item = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  /* 배경 미지정(투명) — Section 배경을 그대로 비춰, 목차 하이라이트가 내용까지 덮이게 함
     (평소엔 Section이 흰색이라 시각 차이 없음) */

  &:not(:last-child) {
    border-bottom: 1px dashed ${COLOR_SYSTEM.get("Gray")?.[200]};
  }
`;

const ItemMeta = styled.div`
  font-size: 10px;
  margin-bottom: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const HtmlContent = ({
  html,
  isTitle,
  itemCode,
}: {
  html: string;
  isTitle?: boolean;
  itemCode?: string;
}) => {
  // 이미지, 스크립트 및 스타일 태그에서 보안 위험 요소를 제거하는 함수
  const sanitizeHtml = (htmlContent: string, itemCodeParam?: string) => {
    if (!htmlContent) return "";

    // 이미지 태그의 src가 절대경로인 경우 상대경로로 변경, http/https인 경우는 유지
    let processedHtml = htmlContent
      // 스크립트 태그 제거
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // 스타일 태그는 제거하지 않지만 외부 스타일시트 링크 제거
      .replace(/<link\b[^<]*(?:(?!<\/link>)<[^<]*)*<\/link>/gi, "")
      // 이벤트 핸들러 제거
      .replace(/on\w+="[^"]*"/gi, "")
      .replace(/on\w+='[^']*'/gi, "");

    // 이미지 플레이스홀더 변환 (itemCode가 있을 때만)
    if (itemCodeParam) {
      processedHtml = processedHtml.replace(
        LABEL_IMAGE_PLACEHOLDER_PATTERN,
        (match, serial) => {
          const imageUrl = getLabelImageUrl(itemCodeParam, serial);
          return `<img src="${imageUrl}" alt="허가사항 이미지 ${serial}" class="label-image" data-serial="${serial}" loading="lazy" />`;
        }
      );
    }

    return processedHtml;
  };

  let sanitizedHtml = sanitizeHtml(html, itemCode);

  const options = {
    replace: (domNode: any) => {
      // 허가사항 이미지 처리 - Image atom 사용
      if (domNode.name === "img" && domNode.attribs?.class?.includes("label-image")) {
        const src = domNode.attribs.src;
        const serial = domNode.attribs["data-serial"] || "";
        const alt = domNode.attribs.alt || `허가사항 이미지 ${serial}`;

        return (
          <Image
            key={`label-img-${serial}`}
            src={src}
            alt={alt}
            showOnError={false}
            objectFit="contain"
            enableHoverZoom={true}
            zoomScale={4}
          />
        );
      }

      // 테이블 관련 요소 처리
      if (domNode.name === "table") {
        domNode.attribs = {
          ...domNode.attribs,
          className: "enhanced-table",
        };
      }

      // caption 요소 처리
      if (domNode.name === "caption") {
        domNode.attribs = {
          ...domNode.attribs,
          className: "enhanced-caption",
        };
      }

      // tr 요소 처리 - 첫번째 행은 헤더처럼 표시
      if (domNode.name === "tr" && domNode.parent && domNode.parent.children) {
        // tr이 tbody의 첫번째 자식인지 확인
        let isFirstRow = false;
        const trChildren = domNode.parent.children.filter((child: any) => child.name === "tr");
        isFirstRow = trChildren.indexOf(domNode) === 0;

        domNode.attribs = {
          ...domNode.attribs,
          className: isFirstRow ? "enhanced-tr-header" : "enhanced-tr",
        };
      }

      // td, th 요소 처리
      if (domNode.name === "td" || domNode.name === "th") {
        // 부모 tr이 첫번째 행인지 확인
        let isHeaderCell = false;
        if (
          domNode.parent &&
          domNode.parent.name === "tr" &&
          domNode.parent.parent &&
          domNode.parent.parent.children
        ) {
          const trChildren = domNode.parent.parent.children.filter(
            (child: any) => child.name === "tr"
          );
          isHeaderCell = trChildren.indexOf(domNode.parent) === 0;
        }

        domNode.attribs = {
          ...domNode.attribs,
          className: isHeaderCell
            ? "enhanced-header-cell"
            : domNode.name === "th"
            ? "enhanced-th"
            : "enhanced-td",
        };
      }
      return undefined; // 기본 변환을 사용하기 위해 undefined 반환
    },
  };

  return (
    <div className={isTitle ? "item-title" : "item-content"}>{parse(sanitizedHtml, options)}</div>
  );
};

const GlobalStyle = styled.div`
  .item-content,
  .item-title {
    /* 기본 스타일 */
    line-height: 1.6;
    font-size: 14px;

    /* HTML 태그 스타일 */
    p {
      margin: 0.5em 0;
    }

    ul,
    ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    li {
      margin: 0.25em 0;
    }

    /* 테이블 래퍼 스타일 */
    .table-wrapper-outer,
    ._table_wrap_out {
      overflow-x: auto;
      margin: 1em 0;
      width: 100%;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .table-wrapper-inner,
    ._table_wrap_in {
      min-width: 100%;
      width: 100%;
      display: table;
    }

    /* 향상된 테이블 스타일 */
    .enhanced-table {
      border-collapse: collapse;
      width: 100%;
      font-size: 14px;
      background-color: white;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
    }

    .enhanced-caption {
      caption-side: top;
      margin-bottom: 10px;
      font-style: italic;
      color: ${COLOR_SYSTEM.get("Gray")?.[600]};
      text-align: left;
      font-size: 12px;
      padding: 0 0 5px 0;
    }

    .enhanced-caption .hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .enhanced-tr-header td,
    .enhanced-tr-header th,
    .enhanced-header-cell {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[600]};
      color: white;
      font-weight: 600;
      padding: 12px;
      text-align: center;
      border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[700]};
      font-size: 13px;
      line-height: 1.4;
    }

    .enhanced-tr-header {
      height: auto;
    }

    .enhanced-tr:last-child td {
      border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
    }

    .enhanced-th {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[100]};
      color: ${COLOR_SYSTEM.get("Blue")?.[700]};
      font-weight: 600;
      padding: 12px;
      text-align: center;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
    }

    .enhanced-td {
      padding: 12px;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
      vertical-align: middle;
      text-align: center;
      word-break: keep-all; /* 단어 사이에서만 줄바꿈 */
      line-height: 1.4;
    }

    /* 짝수 행 스타일 */
    .enhanced-tr:nth-child(even) {
      background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
    }

    /* 테이블 칼럼 스타일 */
    .enhanced-table td:first-child,
    .enhanced-table th:first-child {
      width: 20%; /* 첫 번째 컬럼 너비 조정 */
      word-break: keep-all;
    }

    .enhanced-table td:nth-child(2),
    .enhanced-table th:nth-child(2) {
      width: 25%; /* 두 번째 컬럼 너비 조정 */
    }

    .enhanced-table td:nth-child(3),
    .enhanced-table th:nth-child(3) {
      width: 30%; /* 세 번째 컬럼 너비 조정 */
    }

    .enhanced-table td:last-child,
    .enhanced-table th:last-child {
      width: 25%; /* 마지막 컬럼 너비 조정 */
    }

    /* 테이블 셀 내 단락 스타일 */
    .enhanced-td p,
    .enhanced-header-cell p {
      margin: 0;
      padding: 2px 0;
    }

    /* 호버 효과 */
    .enhanced-tr:hover td {
      background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
    }

    a {
      color: ${COLOR_SYSTEM.get("Blue")?.[500]};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    /* 이미지 스타일 */
    img {
      max-width: 100%;
      height: auto;
      margin: 8px 0;
    }
  }

  .item-title {
    font-weight: 600;
    font-size: 15px;
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
    margin-bottom: 8px;
  }
`;

interface MetaTagProps {
  tagType: "module" | "submodule" | "subnumber";
}

const MetaTag = styled.span<MetaTagProps>`
  padding: 2px 4px;
  border-radius: 4px;
  font-weight: 500;

  ${({ tagType }) => {
    switch (tagType) {
      case "module":
        return `
          background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
          color: ${COLOR_SYSTEM.get("Blue")?.[600]};
          border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
        `;
      case "submodule":
        return `
          background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
          color: ${COLOR_SYSTEM.get("Gray")?.[600]};
          border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
        `;
      case "subnumber":
        return `
          background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
          color: ${COLOR_SYSTEM.get("Gray")?.[500]};
          border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
        `;
      default:
        return `
          background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
          color: ${COLOR_SYSTEM.get("Gray")?.[500]};
          border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
        `;
    }
  }}
`;

const DrugLabelInfo: React.FC<DrugLabelInfoProps> = ({
  labelInfo,
  showMetadata = false,
  itemCode = "",
  productName = "",
  moduleNameMap,
  selectedCodes,
  onTocJump,
  onHeaderMount,
  onHeaderVisible,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 약 내부 목차(TOC) — 코드 필터 반영 2계층. 항목 1개 이하면 버튼 숨김.
  const [tocOpen, setTocOpen] = useState(false);
  const tocRef = useRef<HTMLDivElement>(null);
  const tocItems = useMemo(
    () => buildSectionToc(itemCode, labelInfo, selectedCodes, moduleNameMap),
    [itemCode, labelInfo, selectedCodes, moduleNameMap]
  );
  useEffect(() => {
    if (!tocOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tocRef.current && !tocRef.current.contains(e.target as Node)) setTocOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [tocOpen]);
  const jumpToSection = useCallback(
    (id: string) => {
      setTocOpen(false);
      // 부모(근거자료 패널)가 있으면 보고서 ref와 동일 경로로 스크롤+하이라이트 처리.
      if (onTocJump) {
        onTocJump(id);
        return;
      }
      // 그 외 사용처: 스크롤만(ref 하이라이트 CSS가 없는 화면)
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [onTocJump]
  );

  log(LogLevel.VERBOSE, "DrugLabelInfo rendering/rerendering", {
    itemCode,
    labelInfoLength: labelInfo?.length,
  });

  useEffect(() => {
    log(LogLevel.VERBOSE, "DrugLabelInfo props changed (itemCode)", { itemCode });
  }, [itemCode]);

  useEffect(() => {
    log(LogLevel.VERBOSE, "DrugLabelInfo props changed (labelInfo)", {
      labelInfoLength: labelInfo?.length,
      firstItemModule: labelInfo?.[0]?.module,
    });
  }, [labelInfo]);

  // 헤더 마운트 시 콜백 호출
  useEffect(() => {
    if (headerRef.current && onHeaderMount && itemCode) {      onHeaderMount(itemCode, headerRef.current);
    }
  }, [itemCode, onHeaderMount]);

  // Intersection Observer 설정
  useEffect(() => {
    if (!headerRef.current || !onHeaderVisible || !itemCode) return;

    // 기존 observer 정리
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {        onHeaderVisible(itemCode, entry.isIntersecting, entry.intersectionRatio);
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: "-50px 0px 0px 0px", // sticky 헤더 고려
      }
    );

    observerRef.current.observe(headerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [itemCode, onHeaderVisible]);

  // 프롬프트 생성과 동일한 ID 패턴 생성 함수
  const isHff = itemCode.startsWith("HFF");
  const generateItemId = (item: DrugProductLabel, moduleCode: string) => {
    let baseId: string;
    // NB(주의사항) 및 INGREDI(성분정보) 모듈에서는 submodule을 사용
    if (moduleCode === "NB" || moduleCode === "INGREDI") {
      const submoduleKey = item.submodule || moduleCode;
      baseId = `${submoduleKey}_${item.id}`;
    } else {
      baseId = `${moduleCode}_${item.id}`;
    }
    // 건기식은 HFF_ 접두사를 붙여 백엔드 refId(HFF_NB_12345)와 직접 매칭
    return isHff ? `HFF_${baseId}` : baseId;
  };

  // 모듈 별로 허가사항 그룹화
  const groupedLabels = useMemo(() => {
    if (!labelInfo || !Array.isArray(labelInfo) || labelInfo.length === 0) {
      return {};
    }

    // 모듈별로 그룹화
    return labelInfo.reduce((acc, item) => {
      const module = item.module || "unknown";
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(item);
      return acc;
    }, {} as Record<string, DrugProductLabel[]>);
  }, [labelInfo]);

  // 코드 단위 필터(Phase 3) 적용 — selectedCodes 미전달이면 전체.
  // 비-NB 항목은 module ∈ selectedCodes, NB 항목은 submodule ∈ selectedCodes 일 때만 유지.
  // 항목이 모두 빠진 모듈은 제거 → 섹션 자체가 사라진다(availableModules 기준).
  const filteredGroupedLabels = useMemo(() => {
    if (!selectedCodes) return groupedLabels;
    const codeSet = new Set(selectedCodes);
    const out: Record<string, DrugProductLabel[]> = {};
    for (const [module, items] of Object.entries(groupedLabels)) {
      const kept = items.filter((item) => {
        // NB는 submodule(없으면 "UNDEF"), 그 외는 module 기준 — buildCodeFilterOptions와 동일 규칙
        const code = module === "NB" ? item.submodule || "UNDEF" : item.module || "";
        return codeSet.has(code);
      });
      if (kept.length > 0) out[module] = kept;
    }
    return out;
  }, [groupedLabels, selectedCodes]);

  // 모듈 내용 렌더링 함수
  const renderModuleContent = (moduleCode: string) => {
    if (!filteredGroupedLabels[moduleCode]) {
      return <Text textType="Body2">정보 없음</Text>;
    }

    // subnumber 기준으로 정렬
    const sortedItems = [...filteredGroupedLabels[moduleCode]].sort((a, b) => {
      const numA = parseInt(a.subnumber || "0", 10);
      const numB = parseInt(b.subnumber || "0", 10);
      return numA - numB;
    });

    return sortedItems.map((item) => {
      const itemId = generateItemId(item, moduleCode);
      // SECTION인 경우 detail을 괄호로 감싸기
      const displayDetail = item.submodule === "SECTION" ? `(${item.detail})` : item.detail;
      return (
        <Item key={`${item.id}`} id={itemId}>
          {showMetadata && (
            <ItemMeta>
              <MetaTag tagType="module">{item.module || "unknown"}</MetaTag>
              {item.submodule && <MetaTag tagType="submodule">{item.submodule}</MetaTag>}
              {item.subnumber && <MetaTag tagType="subnumber">{item.subnumber}</MetaTag>}
            </ItemMeta>
          )}
          {/* HTML로 파싱하여 렌더링 */}
          <HtmlContent html={displayDetail} isTitle={item.subnumber === "0"} itemCode={itemCode} />
        </Item>
      );
    });
  };

  // 주의사항(NB) — 하위 submodule을 소제목으로 그룹 렌더(목차 점프 타깃). 코드 필터 반영(filteredGroupedLabels),
  // 본문/목차/필터가 동일하게 SUBMODULE_ORDER 자연순을 쓰도록 정렬.
  const renderCautionsBySubmodule = () => {
    const nbItems = filteredGroupedLabels["NB"];
    if (!nbItems) {
      return <Text textType="Body2">정보 없음</Text>;
    }

    // 하위 모듈별로 그룹화
    const submoduleGroups = nbItems.reduce((acc, item) => {
      const submodule = item.submodule || "unknown";
      if (!acc[submodule]) {
        acc[submodule] = [];
      }
      acc[submodule].push(item);
      return acc;
    }, {} as Record<string, DrugProductLabel[]>);

    // 하위 모듈을 허가사항 자연 순서(SUBMODULE_ORDER)로 정렬 — 목차/코드 필터와 동일 순서
    const subOrder = (s: string) => {
      const i = SUBMODULE_ORDER.indexOf(baseCode(s));
      return i === -1 ? SUBMODULE_ORDER.length : i;
    };

    return Object.entries(submoduleGroups)
      .sort(([a], [b]) => subOrder(a) - subOrder(b))
      .map(([submodule, items]) => {
        const submoduleName =
          SUBMODULE_NAME_MAP[submodule] || SUBMODULE_NAME_MAP[baseCode(submodule)] || submodule;

        // subnumber 기준으로 정렬
        const sortedItems = [...items].sort((a, b) => {
          const numA = parseInt(a.subnumber || "0", 10);
          const numB = parseInt(b.subnumber || "0", 10);
          return numA - numB;
        });

        return (
          <SubSection key={submodule} id={subSectionDomId(itemCode, submodule)}>
          <SubSectionTitle>
            <Text textType="Body1" style={{ fontWeight: 600 }}>
              {submoduleName}
            </Text>
          </SubSectionTitle>
          <div>
            {sortedItems.map((item) => {
              const itemId = generateItemId(item, "NB");
              // SECTION인 경우 detail을 괄호로 감싸기
              const displayDetail = item.submodule === "SECTION" ? `(${item.detail})` : item.detail;
              return (
                <Item key={`${item.id}`} id={itemId}>
                  {showMetadata && (
                    <ItemMeta>
                      <MetaTag tagType="module">{item.module || "unknown"}</MetaTag>
                      {item.submodule && <MetaTag tagType="submodule">{item.submodule}</MetaTag>}
                      {item.subnumber && <MetaTag tagType="subnumber">{item.subnumber}</MetaTag>}
                    </ItemMeta>
                  )}
                  {/* HTML로 파싱하여 렌더링 */}
                  <HtmlContent html={displayDetail} isTitle={item.subnumber === "0"} itemCode={itemCode} />
                </Item>
              );
            })}
          </div>
        </SubSection>
      );
    });
  };

  // 사용 가능한 모듈 목록
  const availableModules = Object.keys(filteredGroupedLabels);

  // 모듈 이름 가져오기 (숫자가 포함된 코드 처리 포함)
  const getModuleName = (moduleCode: string) => {
    // 외부 매핑이 있으면 우선 적용
    if (moduleNameMap && moduleNameMap[moduleCode]) {
      return moduleNameMap[moduleCode];
    }
    // 기본 매핑된 이름이 있으면 사용
    if (MODULE_NAME_MAP[moduleCode]) {
      return MODULE_NAME_MAP[moduleCode];
    }

    // 숫자 제거하고 기본 모듈 코드 찾기
    const match = moduleCode.match(/([A-Z]+)(\d+)?/);
    if (match && match[1] && MODULE_NAME_MAP[match[1]]) {
      return MODULE_NAME_MAP[match[1]];
    }

    // 매핑이 없으면 원래 코드 반환
    return moduleCode;
  };

  return (
    <Container id={`medication-${itemCode}`}>
      <GlobalStyle />

      {/* 코드 정보 표시 - 현재 비활성화 */}
      {/* {(itemCode || standardCode || mainCode) && showMetadata && (
        <CodeInfoContainer>
          <Text textType="Body1" style={{ fontWeight: 600 }}>
            약품 코드 정보
          </Text>
          <CodeGrid>
            {itemCode && (
              <>
                <CodeLabel>품목코드</CodeLabel>
                <CodeValue>{itemCode}</CodeValue>
              </>
            )}
            {standardCode && (
              <>
                <CodeLabel>표준코드</CodeLabel>
                <CodeValue>{standardCode}</CodeValue>
              </>
            )}
            {mainCode && (
              <>
                <CodeLabel>주성분코드</CodeLabel>
                <CodeValue>{mainCode}</CodeValue>
              </>
            )}
          </CodeGrid>
        </CodeInfoContainer>
      )} */}

      {/* 제품명 헤더 */}
      {productName && (
        <ProductSection>
          <ProductHeader ref={headerRef}>
            <div className="header-title-container">
              {itemCode && (
                <Image
                  src={`https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/drugs/pictures/${itemCode}.jpg`}
                  alt={productName}
                  height={28}
                  width={28}
                  objectFit="contain"
                  showOnError={false}
                  enableHoverZoom={true}
                />
              )}
              <h2>{productName}</h2>
            </div>
            <div className="NedrugUrlJump">
              {itemCode && !itemCode.startsWith("HFF") && (
                <a
                  href={`https://nedrug.mfds.go.kr/pbp/CCBBB01/getItemDetail?itemSeq=${itemCode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  허가사항으로 이동
                </a>
              )}
              {tocItems.length > 1 && (
                <TocContainer ref={tocRef}>
                  <Button
                    type="Icon"
                    size="Small"
                    color="Secondary"
                    variant="Ghost"
                    icon="List"
                    onClick={() => setTocOpen((o) => !o)}
                    title="이 약의 항목 목차"
                    iconSize={16}
                    style={{
                      // atom 기본 padding(8px)을 인라인으로 덮어 16 아이콘 주위 여백을 타이트하게
                      padding: "2px",
                      height: "20px",
                      width: "20px",
                      minHeight: "20px",
                      minWidth: "20px",
                      backgroundColor: tocOpen ? COLOR_SYSTEM.get("Gray")?.[100] : "transparent",
                    }}
                  />
                  {tocOpen && (
                    <TocMenu>
                      {tocItems.map((it) => (
                        <TocMenuItem
                          key={it.targetId}
                          $depth={it.depth}
                          onClick={() => jumpToSection(it.targetId)}
                          title={it.label}
                        >
                          {it.label}
                        </TocMenuItem>
                      ))}
                    </TocMenu>
                  )}
                </TocContainer>
              )}
            </div>
          </ProductHeader>
        </ProductSection>
      )}

      {/* 성분 섹션 */}
      {availableModules.includes("INGREDI") && (
        <Section id={sectionDomId(itemCode, "INGREDI")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("INGREDI")}
            </Text>
          </SectionHeader>
          {renderModuleContent("INGREDI")}
        </Section>
      )}

      {/* 효능효과 섹션 */}
      {availableModules.includes("EE") && (
        <Section id={sectionDomId(itemCode, "EE")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("EE")}
            </Text>
          </SectionHeader>
          {renderModuleContent("EE")}
        </Section>
      )}

      {/* 용법용량 섹션 */}
      {availableModules.includes("UD") && (
        <Section id={sectionDomId(itemCode, "UD")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("UD")}
            </Text>
          </SectionHeader>
          {renderModuleContent("UD")}
        </Section>
      )}

      {/* 사용상의 주의사항 섹션 */}
      {availableModules.includes("NB") && (
        <Section id={sectionDomId(itemCode, "NB")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("NB")}
            </Text>
          </SectionHeader>
          {renderCautionsBySubmodule()}
        </Section>
      )}

      {/* 약리작용 섹션 */}
      {availableModules.includes("ACTN") && (
        <Section id={sectionDomId(itemCode, "ACTN")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("ACTN")}
            </Text>
          </SectionHeader>
          {renderModuleContent("ACTN")}
        </Section>
      )}

      {/* 보관 및 취급상의 주의 섹션 */}
      {availableModules.includes("STOR") && (
        <Section id={sectionDomId(itemCode, "STOR")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("STOR")}
            </Text>
          </SectionHeader>
          {renderModuleContent("STOR")}
        </Section>
      )}

      {/* 전문가를 위한 정보 섹션 */}
      {availableModules.includes("EXPR") && (
        <Section id={sectionDomId(itemCode, "EXPR")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("EXPR")}
            </Text>
          </SectionHeader>
          {renderModuleContent("EXPR")}
        </Section>
      )}

      {/* 기타 섹션 */}
      {availableModules.includes("ELSE") && (
        <Section id={sectionDomId(itemCode, "ELSE")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("ELSE")}
            </Text>
          </SectionHeader>
          {renderModuleContent("ELSE")}
        </Section>
      )}

      {/* 미분류 섹션 */}
      {availableModules.includes("UNDEF") && (
        <Section id={sectionDomId(itemCode, "UNDEF")}>
          <SectionHeader>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
              {getModuleName("UNDEF")}
            </Text>
          </SectionHeader>
          {renderModuleContent("UNDEF")}
        </Section>
      )}

      {/* 그 외 모듈 섹션 - 이미 처리된 모듈 외의 다른 모듈이 있으면 표시 */}
      {availableModules
        .filter(
          (module) =>
            !["INGREDI", "EE", "UD", "NB", "ACTN", "STOR", "EXPR", "ELSE", "UNDEF"].includes(module)
        )
        .map((module, index) => {
          // 숫자가 있는 모듈 코드 처리(예: STOR6)
          const baseModule = module.replace(/\d+$/, "");
          // 이미 처리된 기본 모듈이면 건너뛰기
          if (
            ["INGREDI", "EE", "UD", "NB", "ACTN", "STOR", "EXPR", "ELSE", "UNDEF"].includes(
              baseModule
            )
          ) {
            return null;
          }

          return (
            <Section key={index.toString()} id={sectionDomId(itemCode, module)}>
              <SectionHeader>
                <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
                  {getModuleName(module)}
                </Text>
              </SectionHeader>
              {renderModuleContent(module)}
            </Section>
          );
        })
        .filter(Boolean)}
    </Container>
  );
};

export default React.memo(DrugLabelInfo);
